import { useMemo } from 'react';
import { useHasPermissionCallback } from 'hooks/use-has-permission';
import NavbarMenuItem, { type MenuItem } from 'components/layout/navbar/navbar-menu-item';
import NavbarMenuItemSkeleton from 'components/layout/navbar/navbar-menu-item-skeleton';
import NavbarMenuSectionTitle, { type MenuSectionTitle } from 'components/layout/navbar/navbar-menu-section-title';

type ExtendedMenuItem = (MenuItem | MenuSectionTitle) & {
  requiredPermissions?: string[];
  isLoading?: boolean;
  shouldShow?: boolean;
};

interface NavbarMenuListProps {
  items: ExtendedMenuItem[];
}

const NavbarMenuList = ({ items }: NavbarMenuListProps) => {
  const hasPermission = useHasPermissionCallback();

  const processedMenuItems = useMemo(() => {
    return items.map(item => {
      if (!item.requiredPermissions) {
        return {
          ...item,
          shouldShow: item.shouldShow !== undefined ? item.shouldShow : true,
          isLoading: item.isLoading || false
        };
      }

      const permissionStatuses = item.requiredPermissions.map(permission => hasPermission(permission as any));
      const allPermissionsLoaded = permissionStatuses.every(status => !status.isLoading);
      const isAuthorized = permissionStatuses.some(status => status.isAuthorized);

      return {
        ...item,
        shouldShow: allPermissionsLoaded && isAuthorized && (item.shouldShow !== undefined ? item.shouldShow : true),
        isLoading: !allPermissionsLoaded || item.isLoading || false
      };
    });
  }, [items, hasPermission]);

  return (
    <>
      {processedMenuItems.map(menuItem => {
        if (menuItem.isLoading) {
          return <NavbarMenuItemSkeleton key={`skeleton-${menuItem.key}`} />;
        }

        if (menuItem.shouldShow) {
          return 'isSectionTitle' in menuItem ? (
            <NavbarMenuSectionTitle key={menuItem.key} item={menuItem} />
          ) : (
            <NavbarMenuItem key={menuItem.key} item={menuItem} />
          );
        }

        return null;
      })}
    </>
  );
};

export default NavbarMenuList;
export type { ExtendedMenuItem };
